import React, { useState, useEffect, useMemo } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { Typography, Segmented, Card, Space } from "antd";
import {
  ApiOutlined,
  SecurityScanOutlined,
  StopOutlined,
} from "@ant-design/icons";
import OIDCRegForm from "../protocols/OIDCRegForm";
import SAMLRegForm from "../protocols/SAMLRegForm";
import { RegistrationProtocols } from "../../models/registrationProtocols";
import CategoryOption from "../CategoryOption";

const { Title, Paragraph, Text } = Typography;

const NewSSORegistration = ({
  handleSubmit,
  providers,
  formValidation,
  submitFunction,
  onValidationChange,
}) => {
  const [mode, setMode] = useState(RegistrationProtocols.OIDC);

  const [OIDCFormTrigger, setOIDCFormTrigger] = useState(null);
  const [OIDCFormSubmitFunction, setOIDCFormSubmitFunction] = useState(null);
  const [OIDCFormValid, setOIDCFormValid] = useState(false);

  const [SAMLFormTrigger, setSAMLFormTrigger] = useState(null);
  const [SAMLFormSubmitFunction, setSAMLFormSubmitFunction] = useState(null);
  const [SAMLFormValid, setSAMLFormValid] = useState(false);

  const handleAddRegistration = (providerString, data) => {
    const provider = providers.find(
      (el) => el.authenticationProviderIdentifier === providerString,
    );
    handleSubmit(provider?.id, data);
  };

  const handleAddOIDCRegistration = (data) => {
    const uniqueRedirects = Array.from(new Set(data.redirectUris));
    handleAddRegistration(RegistrationProtocols.OIDC, {
      ...data,
      redirectUris: uniqueRedirects,
    });
  };

  const handleAddSAMLRegistration = (data) => {
    handleAddRegistration(RegistrationProtocols.SAML, data);
  };

  useEffect(() => {
    if (mode === RegistrationProtocols.OIDC && OIDCFormTrigger) {
      formValidation(() => OIDCFormTrigger);
    } else if (mode === RegistrationProtocols.SAML && SAMLFormTrigger) {
      formValidation(() => SAMLFormTrigger);
    } else if (mode === RegistrationProtocols.NONE) {
      formValidation(() => () => true);
    }
  }, [mode, OIDCFormTrigger, SAMLFormTrigger, formValidation]);

  // Propagate validation state to parent
  useEffect(() => {
    if (onValidationChange) {
      if (mode === RegistrationProtocols.OIDC) {
        onValidationChange(OIDCFormValid);
      } else if (mode === RegistrationProtocols.SAML) {
        onValidationChange(SAMLFormValid);
      } else if (mode === RegistrationProtocols.NONE) {
        onValidationChange(true); // No SSO is always valid
      }
    }
  }, [mode, OIDCFormValid, SAMLFormValid, onValidationChange]);

  useEffect(() => {
    if (mode === RegistrationProtocols.OIDC) {
      submitFunction(
        () => () => OIDCFormSubmitFunction && OIDCFormSubmitFunction(),
      );
    } else if (mode === RegistrationProtocols.SAML) {
      submitFunction(
        () => () => SAMLFormSubmitFunction && SAMLFormSubmitFunction(),
      );
    } else if (mode === RegistrationProtocols.NONE) {
      submitFunction(() => () => handleSubmit(null, null));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, submitFunction, OIDCFormSubmitFunction, SAMLFormSubmitFunction]);

  const radioOptions = useMemo(
    () => [
      {
        label: (
          <CategoryOption
            icon={<SecurityScanOutlined />}
            title="SAML"
            subtitle="Security Assertion Markup Language"
          />
        ),
        value: RegistrationProtocols.SAML,
      },
      {
        label: (
          <CategoryOption
            icon={<ApiOutlined />}
            title="OIDC"
            subtitle="OpenID Connect"
          />
        ),
        value: RegistrationProtocols.OIDC,
      },
      {
        label: (
          <CategoryOption
            icon={<StopOutlined />}
            title="No SSO"
            subtitle="Create without registration"
          />
        ),
        value: RegistrationProtocols.NONE,
      },
    ],
    [],
  );

  if (!providers?.length) return null;

  return (
    <Space direction="vertical" size={16} style={{ width: "100%" }}>
      <div>
        <Title level={5} style={{ marginTop: 8, marginBottom: 8 }}>
          Which protocol does your application use for authentication?
        </Title>
        <Segmented
          id="registrationProtocol"
          block
          value={mode}
          onChange={(val) => setMode(val)}
          options={radioOptions}
        />
      </div>
      <Card
        style={{
          width: "100%",
          padding: 12,
          transition: "all .2s ease",
        }}
      >
        {mode === RegistrationProtocols.OIDC && (
          <>
            <OIDCRegForm
              submitFunction={handleAddOIDCRegistration}
              isNewApplicationForm
              formValidation={setOIDCFormTrigger}
              handleFormSubmitClick={setOIDCFormSubmitFunction}
              onValidationChange={setOIDCFormValid}
            />
          </>
        )}

        {mode === RegistrationProtocols.SAML && (
          <>
            <SAMLRegForm
              submitFunction={handleAddSAMLRegistration}
              isNewApplicationForm
              formValidation={setSAMLFormTrigger}
              handleFormSubmitClick={setSAMLFormSubmitFunction}
              onValidationChange={setSAMLFormValid}
            />
          </>
        )}

        {mode === RegistrationProtocols.NONE && (
          <>
            <Title level={5} style={{ marginTop: 0, marginBottom: 8 }}>
              No SSO registration
            </Title>
            <Paragraph type="secondary" style={{ marginBottom: 0 }}>
              Please press <Text strong>Submit</Text> to create an application
              without SSO registration.
            </Paragraph>
          </>
        )}
      </Card>
    </Space>
  );
};

NewSSORegistration.propTypes = {
  handleSubmit: PropTypes.func,
  providers: PropTypes.array,
  formValidation: PropTypes.func,
  submitFunction: PropTypes.func,
  onValidationChange: PropTypes.func,
};

export default connect(() => ({}))(NewSSORegistration);
